import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config/apiConfig';

/**
 * Permission Logs — view and revoke cross-tenant access grants.
 * Shows active READ_ONLY / AGGREGATE_ONLY grants issued via the A2A/MCP protocol.
 */
const PermissionLogs = ({ clientConfig }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const clientId = clientConfig?.client_id;

  const fetchGrants = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/permissions?tenant_id=${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setGrants(data.grants || []);
      }
    } catch { /* best-effort */ }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { fetchGrants(); }, [fetchGrants]);

  const handleRevoke = async (permissionId) => {
    setRevoking(permissionId);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/permissions/${permissionId}`, { method: 'DELETE' });
      if (res.ok) {
        setGrants(prev => prev.filter(g => g.permission_id !== permissionId));
      }
    } catch { /* best-effort */ }
    setRevoking(null);
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2 style={{ marginBottom: '0.25rem' }}>🔐 Permission Logs</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        Active cross-tenant access grants for <strong>{clientConfig?.company_name}</strong>.
        Distributors and partners with approved A2A handshakes appear here.
        You can revoke any grant immediately.
      </p>

      <button
        onClick={fetchGrants}
        disabled={loading}
        style={{
          padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #ddd',
          background: '#fff', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.85rem',
        }}
      >
        {loading ? 'Refreshing...' : '🔄 Refresh'}
      </button>

      {grants.length === 0 ? (
        <div style={{
          background: '#f8f9fa', borderRadius: '12px', padding: '3rem',
          textAlign: 'center', color: '#aaa', border: '1px solid #e0e0e0',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🛡️</div>
          <p>No active permission grants. Your data is fully isolated.</p>
          <p style={{ fontSize: '0.8rem' }}>
            When a distributor's Comm-AI requests access via the A2A/MCP protocol,
            approved grants will appear here with their expiry time.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {grants.map(grant => (
            <div key={grant.permission_id} style={{
              background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px',
              padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  {grant.access_level} — {grant.scope || 'inventory'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                  Grantee: <code>{grant.grantee_id}</code> &middot;
                  Granted by: {grant.granted_by || 'mcp_bridge'} &middot;
                  Expires: {grant.expiry ? new Date(grant.expiry).toLocaleString() : 'unknown'}
                </div>
              </div>
              <button
                onClick={() => handleRevoke(grant.permission_id)}
                disabled={revoking === grant.permission_id}
                style={{
                  padding: '0.4rem 1rem', borderRadius: '6px', border: '1px solid #e74c3c',
                  background: '#fff', color: '#e74c3c', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                }}
              >
                {revoking === grant.permission_id ? 'Revoking...' : 'Revoke'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PermissionLogs;
